import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { VictoryChart, VictoryBar, VictoryTheme, VictoryAxis } from 'victory';

import { observer } from 'mobx-react-lite';
import { useParams, useSearchParams } from 'react-router-dom';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import { DateCalendar } from '@mui/x-date-pickers';
import { Button, Chip, Tooltip } from '@mui/joy';

import useClock from 'hooks/useClock';
import useWorkKey from 'hooks/useWorkKey';
import useFHRequest from 'hooks/useFHRequest';

import { ClockProps } from 'stores/clock_store';
import DateUI, { Duration, DurationSeconds, TimeUI } from './Date';
import useCollabs from 'hooks/useCollabs';
import useCurrentIdentity from 'hooks/useCurrentIdentity';
import IdentityAvatar from './IdentityAvatar';
import FancyIcon from './FancyIcon';

const WorkItemRequestTitle = observer(({request_uuid}: {request_uuid: string}) => {
    const { getRequest, fetchRequest } = useFHRequest();

    const request = getRequest(request_uuid);
    useEffect(() => {
        if(!request) {
            fetchRequest(request_uuid);
        }
    }, [request_uuid]);

    if(!request) return null;
    return request.title;
});


const WorkItemCollabTitle = observer(({collab_uuid}: {collab_uuid: string}) => {
    const { getCollab, fetchCollab } = useCollabs();
    const collab = getCollab(collab_uuid);
    const currentIdentity = useCurrentIdentity();
    useEffect(() => {
        if(!collab) {
            fetchCollab(currentIdentity?.uuid, collab_uuid);
            }

    }, [collab_uuid]);

    if(!collab) return null;
    if(collab.variant === 'support') {
        return (
            <Stack>
                <Tooltip title="Support Request (not billable)" arrow>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FancyIcon name="DayOnesSupport" fontSize="sm" color="danger" />
                        <Typography level="body-sm" color="primary">{collab.title}</Typography>
                    </Stack>
                </Tooltip>
            </Stack>
        );
    }
    return collab.title ;
});

export const WorkItemTitleText = (({associated_type, associated_uuid}: {associated_type: string, associated_uuid: string}) => {


    if(associated_type === 'request') {
        return <WorkItemRequestTitle request_uuid={associated_uuid} />;
    }
    if(associated_type === 'collab') {
        return <WorkItemCollabTitle collab_uuid={associated_uuid} />
    }

});

export const WorkItemTitle = (({associated_type, associated_uuid}: {associated_type: string, associated_uuid: string}) => {
    let Title = <WorkItemTitleText associated_type={associated_type} associated_uuid={associated_uuid} />;
    return (
        <Link to={`/${associated_type}/${associated_uuid}`}>
        {Title}
    </Link>
    );

});

const ClockReportSummary = (({ clocks, hideChart=false }: { clocks: ClockProps[], hideChart: boolean }) => {

    const total = clocks.reduce((acc, clock) => {
        if(!clock.time_end) return acc;
        return acc + (dayjs(clock.time_end).diff(dayjs(clock.time_start), 'second'));
    }, 0);
    const billable_total = clocks.reduce((acc, clock) => {
        if(!clock.time_end) return acc;
        // console.log("Checking Billable", clock.variant);
        // If there is no variant, it's billable
        if(clock.variant !== 'billable' && clock.variant !== '') return acc;
        return acc + (dayjs(clock.time_end).diff(dayjs(clock.time_start), 'second'));
    }, 0);
    const nonbillable_total = clocks.reduce((acc, clock) => {
        if(!clock.time_end) return acc;
        if(clock.variant === 'billable') return acc;
        return acc + (dayjs(clock.time_end).diff(dayjs(clock.time_start), 'second'));
    }, 0);

    const ungrouped_data = clocks.map((clock) => {
        return {
            date: dayjs(clock.time_start).format('MM/DD'),
            duration: dayjs(clock.time_end).diff(dayjs(clock.time_start), 'second')
        };
    });

    // console.log(ungrouped_data);

    const grouped_data1 = ungrouped_data.reduce((acc, clock) => {
        if(!acc[clock.date]) {
            acc[clock.date] = 0;
        }
        if(clock.duration) {
            acc[clock.date] += clock.duration;
        }
        return acc;
    }, {} as { [key: string]: number });


    const grouped_data = Object.keys(grouped_data1).map((dat) => {
        return {
            date: dat,
            duration: grouped_data1[dat]
        };
    });

    const dates = Object.keys(grouped_data1);
    return (
        <Stack sx={{ pageBreakInside: "avoid" }}>
            <Typography level="title-lg">Summary</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography level="body-md">Total:</Typography>
                <DurationSeconds level="body-md"  seconds={total} />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography level="body-sm">Billable:</Typography>
                    <DurationSeconds level="body-sm"  seconds={billable_total} />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography level="body-sm">Non Billable:</Typography>
                    <DurationSeconds level="body-sm"  seconds={nonbillable_total} />
                </Stack>
            </Stack>

            { !hideChart &&
            <Stack my={1}>
                <VictoryChart
                    height={200}
                    theme={VictoryTheme.material}>

                    <VictoryAxis
                        // tickValues specifies both the number of ticks and where
                        // they are placed on the axis
                        // tickValues={[1, 2, 3, 4]}
                        style={{ tickLabels: { fontSize: 9 }, axisLabel: { fontSize: 9, fill: 'red' } }}
                        tickFormat={dates}
                    />
                    <VictoryAxis
                        dependentAxis
                        style={{ tickLabels: { fontSize: 8 }, axisLabel: { fontSize: 8, fill: 'red' } }}
                        // tickFormat specifies how ticks should be displayed
                        tickFormat={(x) => x > 3600 ? `${Math.ceil(x / 3600)}h` : `${Math.ceil(x / 60)}m`}
                    />
                    <VictoryBar
                        data={grouped_data}
                        x="date"
                        y="duration" />
                </VictoryChart>

            </Stack>
            }
        </Stack>
    );
});

export const ClockReportClockList = (({ clocks, omitHeader=false }: { clocks: ClockProps[], omitHeader?: boolean }) => {

    return (
        <Stack>
        { omitHeader &&
                <>
                    <Typography level="title-lg">Full Clock List</Typography>
                    <Typography level="body-xs">Every time we clocked in and out</Typography>
                </>
        }
            <Table>
                <thead>
                    <tr>
                        <th>Task</th>
                        <th>Person</th>
                        <th>Time</th>
                        <th>Duration</th>
                    </tr>
                </thead>
                <tbody>
                    {clocks.map((clock) => (
                        <tr key={clock.uuid}>
                            <td ><WorkItemTitle associated_type={clock.associated_type} associated_uuid={clock.associated_uuid} /></td>
                            <td><IdentityAvatar identityKey={clock.identity} size="inline" /></td>
                            <td>
                                <Stack>
                                    <Typography level="body-sm">
                                        <DateUI dateString={clock.time_start} />
                                    </Typography>
                                    { clock.time_end &&
                                    <Typography
                                        startDecorator={<FancyIcon name="DayOnesClockOut" fontSize="sm" sx={{opacity: 0.3}}/>}

                                        level="body-xs">
                                        <TimeUI ts={clock.time_end} />
                                    </Typography>
                                    }
                                </Stack>
                            </td>
                            <td>
                                <Duration start={clock.time_start} end={clock.time_end} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Stack>
    );
});

const WorkItemSum = (({ associated_uuid, clocks }: { associated_uuid: string, clocks: ClockProps[] }) => {
    const Title = <WorkItemTitle associated_type={clocks[0].associated_type} associated_uuid={associated_uuid} />;
    const total = clocks.reduce((acc, clock) => {
        if(!clock.time_end) return 0;
        return acc + (dayjs(clock.time_end).diff(dayjs(clock.time_start), 'second'));
    }, 0);

    return (
        <tr>
            <td>
                {Title}
            </td>
            <td>
            <Typography><DurationSeconds seconds={total} /></Typography>
            </td>
        </tr>
    );
});


const ClockReportByWorkItem = (({ clocks }: { clocks: ClockProps[] }) => {
    const work_uuids = [...new Set(clocks.map((clock) => clock.associated_uuid))];
    return (
        <Stack sx={{ pageBreakInside: "avoid" }}>
            <Typography level="title-lg">Task Breakdown</Typography>
            <Typography level="body-xs">Where did we spend our time?</Typography>
            <Table>
                <thead>
                <tr>
                    <th>Task</th>
                    <th>Duration</th>
                </tr>
                </thead>

                <tbody>
                {work_uuids.map((value: string) =>
                    <WorkItemSum
                        key={value}
                        associated_uuid={value}
                        clocks={clocks.filter((clock) => clock.associated_uuid === value)} />
                )}
                </tbody>
            </Table>
        </Stack>
    );
});

const IdentitySum = (({ identity, clocks }: { identity: string, clocks: ClockProps[] }) => {

    const total = clocks.reduce((acc, clock) => {
        if(!clock.time_end) return 0;
        return acc + (dayjs(clock.time_end).diff(dayjs(clock.time_start), 'second'));
    }, 0);

    return (
        <tr>
            <td>
                <IdentityAvatar identityKey={identity} size="inline" />
            </td>
            <td>
            <Typography><DurationSeconds seconds={total} /></Typography>
            </td>
        </tr>
    );
});

const ClockReportByIdentity = (({ clocks }: { clocks: ClockProps[] }) => {
    const identities = [...new Set(clocks.map((clock) => clock.identity))];
    return (
        <Stack>
            <Typography level="title-lg">People</Typography>
            <Typography level="body-xs">Who did the work?</Typography>
            <Table>
                <thead>
                    <tr>
                        <th>Person</th>
                        <th>Duration</th>
                    </tr>
                </thead>

                <tbody>
                    {identities.map((value: string) =>
                        <IdentitySum key={value}
                            identity={value}
                            clocks={clocks.filter((clock) => clock.identity === value)} />
                    )}
                </tbody>
            </Table>
        </Stack>
    );
});

export const ClockReportContent = (({ clocks, hideChart=false }: { clocks: ClockProps[], hideChart?: boolean }) => {
    return (
        <Stack spacing={5} pb={5}>
            <ClockReportSummary clocks={clocks} hideChart={hideChart} />
            <ClockReportByIdentity clocks={clocks} />
            <ClockReportByWorkItem clocks={clocks} />
            <ClockReportClockList clocks={clocks} />
        </Stack>
    );
});

export const ClockReportClient = observer(() => {
    const { identityKey } = useParams();
    if(!identityKey) return null;
    return <ClockReportShared type="client" identityKey={identityKey} />;
});

export const ClockReportAssistant = observer(() => {
    const { identityKey } = useParams();
    if(!identityKey) return null;
    return <ClockReportShared type="assistant" identityKey={identityKey} />;
});

const ClockReportShared = observer(({ type, identityKey }: { type: string, identityKey: string }) => {

    const { fetchClocksByClient, fetchClocksByAssistant } = useClock();
    const workKey = useWorkKey();
    const [searchParams, setSearchParams] = useSearchParams();

    const st = searchParams.get('period_start');
    const et = searchParams.get('period_end');
    const currentMonth = dayjs();
    const [period_start, setPeriodStart] = useState(st ? dayjs(st) : currentMonth.startOf('day'));
    const [period_end, setPeriodEnd] = useState(et ? dayjs(et) : currentMonth.endOf('day'));
    const [loading, setLoading] = useState(false);
    const [showPickers, setShowPickers] = useState(false);

    const [clocks, setClocks] = useState([] as ClockProps[]);
    const [firstFetched, setFirstFetched] = useState(false);
    const defaultChip = searchParams.get('default');
    const [selectedChip, setSelectedChip] = useState(defaultChip);
    // const [formUpdated, setFormUpdated] = useState(false);

    // if periodStart and periodEnd are the same day, hide the chart
    const hideChart = period_start.isSame(period_end, 'day');

    useEffect(() => {
        const fmt = 'YYYY-DD-MM';
        document.title = `Clock Report ${period_start.format(fmt)} - ${period_end.format(fmt)}`;
    }, [period_end, period_start]);

    useEffect(() => {
        defaultChip && quickDate(defaultChip);
    }, [defaultChip]);

    useEffect(() => {
        if (firstFetched) {
            return;
        }
        setFirstFetched(true);
        if (period_start && period_end) {
            console.log("Loading clocks in use effect", period_start, period_end);
            loadClocks([]);
        }
    }, [period_start, period_end]);

    // console.log("period_end", period_end);
    function clickRunReport() {
        console.log("Clearing clocks");
        setClocks([]);
        loadClocks([]);
    }

    function loadClocks(existing_clocks: ClockProps[], cursor: string = '') {
        if (!identityKey) return;
        setLoading(true);
        const ps = period_start.startOf('day').toISOString();
        const pe = period_end.endOf('day').toISOString();
        // const ps = period_start.toISOString();
        // const pe = period_end.toISOString();

        console.log("timing ps", ps);
        console.log("timing pe", pe);
        if ((!searchParams.get('period_start') || !searchParams.get('period_end')) ||
            ((searchParams.get('period_start') != ps) ||
                (searchParams.get('period_end') != pe))) {

            setSearchParams({
                period_start: ps,
                period_end: pe
            });
        }

        // console.log("period_start", period_start.toISOString());
        // console.log("period_end", period_end.toISOString());

        const fn = type == 'client' ? fetchClocksByClient : fetchClocksByAssistant;
        //    fn(workKey, identityKey, period_start.toISOString(), period_end.toISOString(), cursor)
        console.log("timing fn ps:", ps);
        console.log("timing fn pe:", pe);
        fn(workKey, identityKey, ps, pe, cursor)
            .then((data) => {

                // existing_clocks are provided when we have a cursor
                let newClocks = [...existing_clocks];
                console.log("existing clocks", existing_clocks);
                console.log("data.items", data.items);
                data.items.forEach((clock) => {

                    console.log("Checking clock", clock.uuid, clock.time_start);
                    if (!newClocks.find((c) => c.uuid === clock.uuid)) {
                        // verify the time

                        newClocks.push(clock);
                        // console.log("Checking clock", clock.uuid, clock.time_start);
                        // console.log("Checking clock ps", ps);
                        // console.log("Checking clock pe", pe);
                        // if (dayjs(clock.time_start).isAfter(dayjs(ps)) && dayjs(clock.time_start).isBefore(dayjs(ps))) {
                        //     console.log("Clock in range", clock.uuid);
                        //     newClocks.push(clock);
                        // }
                        // else {
                        //     console.log("Clock not in range", clock.time_start);
                        // }
                        // // newClocks.push(clock);
                    }
                    else {
                        console.log("clock uuid not in newclocks");
                    }
                });

                console.log("Setting clocks,", newClocks);
                setClocks(newClocks);
                if (data.cursor) {
                    loadClocks(newClocks, data.cursor);
                }
                else {
                    setLoading(false);
                }
                console.log(data);
            })
    }

    function quickDate(t: string) {
        setSelectedChip(t);
        if (t == 'custom') {
            setShowPickers(!showPickers);
        }
        if (t === 'today') {
            setPeriodStart(dayjs().startOf('day'));
            setPeriodEnd(dayjs().endOf('day'));
        }
        if (t === 'yesterday') {
            setPeriodStart(dayjs().startOf('day').subtract(1, 'day'));
            setPeriodEnd(dayjs().endOf('day').subtract(1, 'day'));
        }
        if (t === 'this week') {
            setPeriodStart(dayjs().startOf('week'));
            setPeriodEnd(dayjs().endOf('week'));
        }
        if (t === 'last week') {
            setPeriodStart(dayjs().startOf('week').subtract(1, 'week'));
            setPeriodEnd(dayjs().endOf('week').subtract(1, 'week'));
        }
        if (t === 'this month') {
            setPeriodStart(dayjs().startOf('month'));
            setPeriodEnd(dayjs().endOf('month'));
        }
        if (t === 'last month') {
            setPeriodStart(dayjs().startOf('month').subtract(1, 'month'));
            setPeriodEnd(dayjs().endOf('month').subtract(1, 'month'));
        }
    }
    // const { clockStore } = useStores();
    // const { clock } = clockStore;
    function _sc(t: string) {
        // console.log(t, selectedChip, t == selectedChip);
        return (t == selectedChip) ? 'solid' : 'outlined';
    }
    const chips = ['today', 'yesterday', 'this week', 'last week', 'this month', 'last month', 'custom'];

    const fmt = 'MM/DD/YYYY';
    return (
        <Stack>
            <Stack my={2}>
                <Typography level="title-lg">{period_start.format(fmt)} - {period_end.format(fmt)}</Typography>
                <Typography level="body-xs">This data may be incomplete.</Typography>
            </Stack>
            <Stack spacing={1} mt={2} className="no-print">
                <Stack direction="row" spacing={0.5} sx={{ overflowY: "scroll", flexWrap: 'wrap' }} useFlexGap>
                    {chips.map((c) =>
                        <Chip sx={{ textTransform: 'capitalize' }} variant={_sc(c)} onClick={() => quickDate(c)}>{c}</Chip>
                    )}

                </Stack>
            </Stack>

            {showPickers &&
                <Stack direction="row" spacing={2} className="no-print">
                    <Stack>
                        <Typography>Start</Typography>

                        <DateCalendar
                            defaultValue={period_start}
                            onChange={(date) => setPeriodStart(date)} />
                    </Stack>
                    <Stack>
                        <Typography>End</Typography>

                        <DateCalendar
                            defaultValue={period_end}
                            onChange={(date) => setPeriodEnd(date)} />

                    </Stack>
                </Stack>
            }


            <Stack my={3} className="no-print">
                <Stack direction="row">
                    <Button disabled={loading} size="sm" onClick={clickRunReport}>Run Report</Button>
                </Stack>
            </Stack>

            {clocks.length > 0 &&
                <Stack spacing={3}>
                <ClockReportContent clocks={clocks} hideChart={hideChart}/>
                </Stack>
            }
        </Stack>
    );
});

export function ClockReportRequest() {

    const { requestKey } = useParams();
    const currentIdentity = useCurrentIdentity();
    const {
        fetchClocksForRequest, getClocksForRequest,
    } = useClock();
    const clocks = requestKey ? getClocksForRequest(requestKey) : [];
    useEffect(() => {
        if(!currentIdentity?.uuid) return;
        if(!requestKey) return;
            fetchClocksForRequest(requestKey, currentIdentity?.uuid);

    }, [requestKey, currentIdentity?.uuid]);

    return (
        <ClockReportContent clocks={clocks} />
    );
}

export function ClockReportCollab() {

    const { collabKey } = useParams();
    const currentIdentity = useCurrentIdentity();
    const {
        fetchClocksForCollab, getClocksForCollab,
    } = useClock();
    const clocks = collabKey ? getClocksForCollab(collabKey) : [];
    useEffect(() => {
        if(!currentIdentity?.uuid) return;
        if(!collabKey) return;
            fetchClocksForCollab(collabKey, currentIdentity?.uuid);

    }, [collabKey, currentIdentity?.uuid]);

    return (
        <ClockReportContent clocks={clocks} />
    );
}

export default ClockReportClient;
